import memoize from 'lodash/memoize';

import { checkIsNarrowSymbolSupported } from './browser';
import { iso4217CurrencyCodes } from './currency/constants';

export const findCurrencyInfoByCode = memoize((currencyCode: string) => iso4217CurrencyCodes.find(({ code }) => code === currencyCode));
export const findCurrencyInfoBySymbol = memoize((currencySymbol: string) => iso4217CurrencyCodes.find(({ symbol }) => symbol === currencySymbol));
export const isIso4217Currency = (currencyCode: string) => !!findCurrencyInfoByCode(currencyCode);

/**
 * Format price based on locale and currency
 * @param price - price in the smallest unit of the currency
 * @param locale - locale of the merchant/store like id-ID, en-US, etc
 * @param currencyCodeOrSymbol - currency code like IDR, SGD, etc or currency symbol like Rp
 * @returns formatted price like Rp 1.000,00
 */
export const formatPrice = (price: number, locale: string, currencyCodeOrSymbol: string) => {
  if (isNaN(price)) {
    console.warn(`price ${price} is invalid in formatPrice function, please make sure you have set the price in the app`);
    return [currencyCodeOrSymbol, price].filter(Boolean).join(' ') || 'N/A';
  }
  if (!locale) {
    console.warn(`locale is not defined in formatPrice function, please make sure you have set the locale in the app`);
    locale = 'en-US';
  }
  if (!currencyCodeOrSymbol) {
    throw new Error(`currency is not defined in formatPrice function`);
  }

  const currencyInfo = findCurrencyInfoByCode(currencyCodeOrSymbol) || findCurrencyInfoBySymbol(currencyCodeOrSymbol);

  if (!currencyInfo) {
    throw new Error(`currency ${currencyCodeOrSymbol} is not supported in formatPrice function`);
  }

  let options: Intl.NumberFormatOptions = {};
  options.style = 'currency';
  options.currency = currencyInfo.code;

  // @ts-ignore: https://github.com/microsoft/TypeScript/issues/48319
  options.currencyDisplay = checkIsNarrowSymbolSupported() ? 'narrowSymbol' : 'symbol';

  // price is in the smallest unit of the currency
  price = price / Math.pow(10, currencyInfo.decimals);

  // set locale to currencyInfo.locale if it's defined, otherwise it looks odd when combining USD with id-ID
  // TODO: is this fine? how can this be improved? This exists because a DEMO merchant can use multiple currencies with different locales, combining USD with demo merchant's locale of id-ID looks wrong
  locale = currencyInfo?.locale || locale;

  options = handlePriceMinimumFractionDigits(locale, currencyInfo, options);

  const formattedPrice = new Intl.NumberFormat(locale, options).format(price);
  if (currencyInfo) return formattedPrice;
};
export const formatDate = (printAt: string, locale: string) => {
  const date = new Date(printAt);
  if (!isFinite(date.getTime())) {
    return printAt;
  }
  const formattedTime = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const formattedDate = date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });

  return `${formattedDate} ${formattedTime}`;
};
/**
 * Handle minimumFractionDigits options for formatPrice
 * @param locale - locale of the merchant/store like id-ID, en-US, etc
 * @param currencyInfo - currency info from ISO 4217
 * @param options - Intl.NumberFormatOptions
 * @returns Intl.NumberFormatOptions
 */
function handlePriceMinimumFractionDigits(locale: string, currencyInfo: typeof iso4217CurrencyCodes[number], options: Intl.NumberFormatOptions) {
  /*
    minimumFractionDigits is set to 0 when we don't want to show the redundant entaling cents.
    for Indonesia and Korea, we don't want to show the cents.
  */
  if (currencyInfo) {
    options.minimumFractionDigits = currencyInfo.decimals;
    // handling special case for IDR
    // even though IDR has 2 decimals in the ISO 4217 standard, we don't want to show the cents
    if (currencyInfo.code === 'IDR') {
      options.minimumFractionDigits = 0;
    }
  } else if (['id', 'ko'].includes(locale?.split('-')[0])) {
    options.minimumFractionDigits = 0;
  } else {
    options.minimumFractionDigits = 2;
  }
  return options;
}

// Only allowing paste when it's number
export const pasteNumberValidation = (e: ClipboardEvent) => {
  const clipboardText = e.clipboardData?.getData('text') ?? '';
  if (!/^[0-9]+$/.test(clipboardText)) e.preventDefault();
};

// Only allowing input when it's number
export const inputNumberValidation = (e: KeyboardEvent) => {
  const allowedKeys = ['Backspace', 'Delete', 'Home', 'End', 'ArrowRight', 'ArrowLeft'];

  if (!/^[0-9]+$/.test(e.key) && !allowedKeys.includes(e.key) && !(e.key === 'v' && e.ctrlKey)) {
    e.preventDefault();
  }
};

export const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

// The _.xor() method returns an array of the symmetric difference of given arrays
// same as lodash.xor
export const xor = <T>(arr1: T[], arr2: T[]) => {
  const set = new Set<T>([...arr1, ...arr2]);
  return [...set].filter((value) => !arr1.includes(value) || !arr2.includes(value));
};

/**
 * transform price to currency's smallest unit based on the currency code
 * e.g. 1.23 => 123 for USD, 123 => 12300 for IDR, 123 => 123 for JPY
 *
 * @param price  price in smallest unit of the currency
 * @param priceMultiplier  multiplier of the price, e.g. if 100 is provided as value for $1.00, the multiplierOfPrice is 2. If 1 is provided as value for $1.00, the multiplierOfPrice is 0.
 * @param currencyCodeOrSymbol ISO 4217 currency code
 * @returns
 */
export const toSmallestCurrencyUnit = (price: number | string, priceMultiplier: number, currencyCodeOrSymbol: string) => {
  if (isNaN(Number(price))) return price;
  const currencyInfo = findCurrencyInfoByCode(currencyCodeOrSymbol.toUpperCase()) || findCurrencyInfoBySymbol(currencyCodeOrSymbol);
  if (!currencyInfo) throw new Error(`currency ${currencyCodeOrSymbol} is not supported`);
  const { decimals } = currencyInfo;
  const result = Math.round((Number(price) / Math.pow(10, priceMultiplier)) * Math.pow(10, decimals));
  return result;
};

export function padTo2Digits(num: number) {
  return num.toString().padStart(2, '0');
}
